import { Control, Controller } from 'react-hook-form';
import { realisation as rules } from 'further-ui/validations';
import useConsiderationCalculations, {
  ConsiderationPerUnitType,
  ConsiderationTotalType,
} from '../../hooks/useConsiderationCalculations';
import FormField from 'further-ui/components/FormField';
import { Row } from 'further-ui/components/Layout';
import NumberInput from 'further-ui/components/NumberInput';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { InputAdornment, Typography } from '@mui/material';

type Props = {
  control: Control<rules.CreateRealisationFormValues>;
  index: number;
  currencyCode: CurrencyCode;
  disabled: boolean;
};

const ManualEarnoutConsiderationRow: React.FC<Props> = ({
  control,
  index,
  currencyCode,
  disabled,
}) => {
  const { onManualConsiderationChange, setFieldFocus } =
    useConsiderationCalculations();

  return (
    <FormField
      label="Earnout consideration"
      labelSize="sm"
      tooltip="This is the visible future consideration that is not yet received by the fund following the realisation but that the fund expects to receive from an earnout or delayed consideration structure. Any earnout consideration actually received should be removed from this row and added to the received consideration total. This field is record-keeping purposes only and will not result in a general ledger entry."
      layout="horizontal"
      breakpoint="md"
    >
      <Row spacing="sm">
        <Controller
          name={`holdingClassRealisation.${index}.cashConsiderationAmounts.earnoutConsiderationPerUnit`}
          control={control}
          render={({
            field: { ref, onChange, ...rest },
            fieldState: { error },
          }) => (
            <NumberInput
              {...rest}
              currencyStartAdornment
              placeholder="Enter amount"
              disabled={disabled}
              currency={currencyCode}
              decimals={10}
              min={0}
              fullWidth
              onChange={(value) => {
                onChange(value);
                onManualConsiderationChange(
                  value ?? 0,
                  ConsiderationPerUnitType.Earnout,
                  index,
                );
              }}
              onFocus={() => {
                setFieldFocus(ConsiderationPerUnitType.Earnout);
              }}
              error={!!error}
              helperText={error?.message}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="body2">Per unit</Typography>
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
        <Controller
          name={`holdingClassRealisation.${index}.cashConsiderationAmounts.earnoutConsiderationTotal`}
          control={control}
          render={({
            field: { ref, onChange, ...rest },
            fieldState: { error },
          }) => (
            <NumberInput
              {...rest}
              currencyStartAdornment
              placeholder="Enter amount"
              disabled={disabled}
              currency={currencyCode}
              decimals={2}
              min={0}
              fullWidth
              onChange={(value) => {
                onChange(value);
                onManualConsiderationChange(
                  value ?? 0,
                  ConsiderationTotalType.Earnout,
                  index,
                );
              }}
              onFocus={() => {
                setFieldFocus(ConsiderationTotalType.Earnout);
              }}
              error={!!error}
              helperText={error?.message}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="body2">Total</Typography>
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
        />
      </Row>
    </FormField>
  );
};

export default ManualEarnoutConsiderationRow;
